import { ArrowUpRight } from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { localizePath, type LocaleCode, useI18n } from "@/i18n";
import { images } from "@/config/images";
import { serviceBySlug } from "@/config/services";

const SYSTEM_IMAGES = [images.shingles, images.metal, images.flat];

const SYSTEM_SERVICE_SLUGS = [
  "asphalt-shingle-replacement",
  "metal-roof-replacement",
  "flat-roof-replacement-sbs-tpo-pvc",
] as const;

const SYSTEM_LINK_LABEL: Record<LocaleCode, string> = {
  "en-ca": "View this roof system",
  "zh-cn": "查看此屋顶系统",
  "zh-tw": "查看此屋頂系統",
  "pa-in": "ਇਹ ਛੱਤ ਪ੍ਰਣਾਲੀ ਵੇਖੋ",
  "ko-kr": "이 지붕 시스템 보기",
  "fa-ir": "مشاهدهٔ این سیستم سقف",
  "ar-sa": "عرض نظام السقف هذا",
};

const HUB_LINK_LABEL: Record<LocaleCode, string> = {
  "en-ca": "Compare roof systems",
  "zh-cn": "比较屋顶系统",
  "zh-tw": "比較屋頂系統",
  "pa-in": "ਛੱਤ ਪ੍ਰਣਾਲੀਆਂ ਦੀ ਤੁਲਨਾ ਕਰੋ",
  "ko-kr": "지붕 시스템 비교",
  "fa-ir": "مقایسه سیستم‌های سقف",
  "ar-sa": "قارن أنظمة الأسقف",
};

export function RoofComparison() {
  const { t, locale } = useI18n();
  const cols = t.comparison.columns;

  return (
    <section id="roof-systems" className="section-y bg-muted/50 border-y border-border">
      <div className="container-page">
        <SectionHeader
          eyebrow={t.comparison.eyebrow}
          title={t.comparison.title}
          body={t.comparison.body}
        />

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {t.comparison.systems.map((system, i) => {
            const img = SYSTEM_IMAGES[i]!;
            const service = serviceBySlug[SYSTEM_SERVICE_SLUGS[i] ?? SYSTEM_SERVICE_SLUGS[0]];
            return (
              <article
                key={system.name}
                className="flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-card"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  width={img.width}
                  height={img.height}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                />
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="display-3 text-foreground">{system.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{system.summary}</p>

                  <dl className="mt-6 space-y-4 border-t border-border pt-6 text-sm">
                    <Row label={cols.application} value={system.application} />
                    <Row label={cols.character} value={system.character} />
                    <Row label={cols.maintenance} value={system.maintenance} />
                    <Row label={cols.investment} value={system.investment} />
                    <Row label={cols.performance} value={system.performance} />
                  </dl>

                  {service ? (
                    <a
                      href={localizePath(service.path, locale)}
                      className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline"
                    >
                      {SYSTEM_LINK_LABEL[locale]}
                      <ArrowUpRight className="size-4" aria-hidden="true" />
                    </a>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-8">
          <a
            href={localizePath("/roof-systems", locale)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground underline-offset-4 hover:underline"
          >
            {HUB_LINK_LABEL[locale]}
            <ArrowUpRight className="size-4" aria-hidden="true" />
          </a>
        </div>

        <p className="mt-8 max-w-3xl text-xs leading-relaxed text-muted-foreground">
          {t.comparison.disclaimer}
        </p>
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[0.68rem] tracking-[0.14em] uppercase text-muted-foreground">{label}</dt>
      <dd className="mt-1 leading-relaxed text-foreground">{value}</dd>
    </div>
  );
}
